import { useRef, useEffect, useState, useCallback, useMemo, memo, forwardRef } from 'react';
import { Swords, LogOut, Pause, Zap, Search, Eye, RotateCcw, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { nessunoHaGiocato, POSTO_MUTO } from '../../utils/tournament-helpers';
import { PlayerProfileOverlay } from '../profile/PlayerProfileOverlay';
import { isGuestUsername } from '../../utils/guest';
import { formatTime } from '@draughts/shared';
import { decimale } from '../../utils/formato-data';
import { useExpiryCountdown } from '../../hooks/useExpiryCountdown';
import type { ArenaStandingEntry, ArenaAvversarioEscluso } from '../../types/arena';
import { DELTA_DISPLAY_MS, SCROLL_DELAY_MS } from '../../constants/timing';
import { PAGINA_CLASSIFICA } from '../../constants/arena';
import { ScoreSheet, CAPIENZA_A_360, type EsitoPedina } from '../shared/ScoreSheet';
import './ArenaStandings.css';
import { PedinaPodio, medagliaDelPosto } from '../shared/PedinaPodio';

/** Il saldo col segno: «+1», «−2» (il meno tipografico), «0». Come nel girone. */
function conSegno(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `\u2212${Math.abs(n)}`;
  return '0';
}

interface ArenaStandingsProps {
  standings: ArenaStandingEntry[];
  userId: string | undefined;
  isActive: boolean;
  /**
   * Chi non mi puo' essere proposto come avversario adesso, e fino a quando.
   *
   * Non e' un dato della classifica — la classifica e' uguale per tutti —
   * quindi arriva a parte, per socket. Serve a spiegare l'attesa: senza, chi
   * sta in coda vede il tempo passare e non sa perche'.
   */
  excluded?: ArenaAvversarioEscluso[];
  /**
   * Quante righe in testa portano una posizione del server, e non un numero
   * contato dal client. Solo su quelle si accendono le frecce ▲▼.
   *
   * Il valore di riposo è la pagina del broadcast, che è il caso della
   * classifica viva. È scelto perché sbaglia dalla parte del silenzio: un
   * quarto punto di chiamata che se ne dimenticasse perderebbe delle frecce
   * vere oltre la ventesima riga — una mancanza, che si nota — invece di
   * riaccendere quelle inventate dalla rinumerazione della coda, che sono
   * una bugia e non si notano affatto.
   */
  righeDalServer?: number;
  /**
   * Quante righe ha la graduatoria in tutto — non quanti iscritti ci sono.
   *
   * Sono due insiemi diversi: chi preme «Esci» dopo aver giocato esce dagli
   * iscritti e resta in graduatoria. La fascia «+N altri giocatori» si governa
   * su questo numero, perché governarla sugli iscritti in un'arena da sessanta
   * dove quarantacinque si sono ritirati dava `15 > 20` — falso — e le righe
   * dopo la ventesima non avevano più modo di essere chieste.
   *
   * Facoltativo perché il tipo del filo non può garantirlo: durante un deploy
   * un client nuovo può parlare con un server vecchio. Senza, la fascia non
   * dichiara nessuna quantità — vedi `codaDaOffrire`.
   */
  righeInClassifica?: number;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /**
   * Apre la partita di quel giocatore da spettatore. Senza, l'occhio non si
   * disegna affatto: una porta senza nessuno dietro è peggio di nessuna porta.
   */
  onSpectatePlayer?: (userId: string) => void;
}

/**
 * Che cosa dire sotto la classifica. Sono due domande, e prima erano un numero
 * solo: **quante ne mancano** (una quantità) e **ce n'è dell'altra** (un sì o
 * un no). Si può sapere la seconda senza sapere la prima, e il difetto stava
 * tutto lì.
 *
 * - Col numero della graduatoria in mano si dicono tutt'e due: `undefined` se
 *   non manca niente, altrimenti quante.
 * - Senza — cioè durante un deploy, quando un client nuovo parla con un server
 *   vecchio — l'unica cosa che si sa è se **l'ultima pagina è tornata piena**.
 *   Allora si offre di guardare, e **non si dichiara nessuna quantità**:
 *   `{ mancanti: undefined }`.
 *
 * Il ripiego di prima la quantità se la inventava — `max(iscritti, righe + 1)`
 * — ed era falsa sempre: prometteva «+1» dove ne mancavano quaranta, e in
 * un'arena con esattamente venti righe e venti iscritti prometteva
 * «+1 altro giocatore» **per sempre**, dietro un bottone che chiedeva una
 * pagina vuota e non cambiava niente. Peggio del comportamento che sostituiva.
 *
 * «Pagina piena» è un multiplo esatto della pagina, non solo la prima: dopo un
 * «Mostra altri» andato a buon fine ce ne sono due, e la terza va offerta lo
 * stesso — il vecchio ripiego qui si spegneva e seppelliva la coda. Si sbaglia
 * per silenzio in un caso raro (una riga della pagina nuova già a schermo
 * viene scartata come doppione e il conto perde il multiplo): una fascia in
 * meno finché il numero vero non arriva, che è meglio di un numero falso.
 */
function codaDaOffrire(
  righeInClassifica: number | undefined,
  righeASchermo: number,
  righeDalServer: number,
): { mancanti: number | undefined } | undefined {
  if (righeInClassifica != null) {
    return righeInClassifica > righeASchermo
      ? { mancanti: righeInClassifica - righeASchermo }
      : undefined;
  }
  const paginaPiena =
    righeDalServer > 0 && righeASchermo > 0 && righeASchermo % righeDalServer === 0;
  return paginaPiena ? { mancanti: undefined } : undefined;
}

/** Sopra questa soglia scorrere per cercarsi non è una risposta. */
const SOGLIA_RICERCA = 20;

/**
 * Lo score sheet dalla cifra alla forma. La cifra era il punteggio della
 * partita (0 persa, 1 patta, 2 vinta, 3 in serie, 4 col boost, 6 con
 * entrambi) e andava imparata: un disegno che ha bisogno di una legenda ha
 * già perso, e infatti accanto ce n'era una.
 *
 * Le pedine il significato lo portano nella forma — piena vinta, mezza
 * patta, vuota persa — quindi si leggono senza istruzioni, e funzionano
 * anche per chi i colori non li distingue.
 *
 * La quarta è il motivo per cui non sono tre: una vittoria che è valsa più
 * di due punti (serie, boost o entrambi) è l'unica cosa in tutta la riga su
 * cui si può decidere qualcosa, e ridurla a "vinta" la butterebbe via.
 */
function esitoDi(cifra: number): EsitoPedina {
  if (cifra === 0) return 'loss';
  if (cifra === 1) return 'draw';
  return cifra > 2 ? 'winPlus' : 'win';
}

/**
 * Il distacco dalla riga vicina, che è la domanda che ci si fa guardando una
 * classifica e che finora andava fatta a mente sottraendo due numeri.
 *
 * Chi non è primo guarda in su ("quanto mi manca"); il primo guarda in giù
 * ("quanto ho di margine"), perché per lui non esiste un sopra.
 */
type Distacco =
  | { verso: 'salire'; punti: number }
  | { verso: 'vantaggio'; punti: number };

function calcolaDistacco(
  standings: ArenaStandingEntry[],
  userId: string | undefined,
): Distacco | undefined {
  if (!userId) return undefined;
  const i = standings.findIndex((s) => s.userId === userId);
  if (i < 0) return undefined;

  const io = standings[i];
  if (!io) return undefined;
  const sopra = i > 0 ? standings[i - 1] : undefined;
  const sotto = standings[i + 1];

  /* A pari punti non si dice niente: il distacco è zero, "0 punti per salire"
   * sarebbe falso (si sale con lo spareggio), e dirlo a parole occupava una
   * riga per un'informazione su cui non si può fare niente. */
  if (sopra) {
    const punti = sopra.points - io.points;
    return punti > 0 ? { verso: 'salire', punti } : undefined;
  }
  if (sotto) {
    const punti = io.points - sotto.points;
    return punti > 0 ? { verso: 'vantaggio', punti } : undefined;
  }
  return undefined;
}

/**
 * Le frecce di posizione, e solo dove i numeri sono del server.
 *
 * Nella pagina di dettaglio il broadcast si ferma alla ventesima riga: quello
 * che sta sotto è la coda caricata col bottone, che lo store rinumera di fila
 * (`fondiClassifica` in `arena-store.ts`). Quei numeri cambiano quando cambia
 * *quante righe stanno sopra*, e da qui non c'è modo di sapere se è perché
 * qualcuno è sceso davvero o perché una riga è stata espulsa dalla
 * classifica — chi si ritira senza aver giocato sparisce dal conteggio del
 * server, la sua riga resta a schermo, e finiva in testa alla coda con un ▼9
 * addosso per un movimento mai avvenuto.
 *
 * Quindi si guardano solo le righe che portano una posizione del server: né
 * si disegna una freccia per le altre, né si tiene il loro numero come
 * termine di paragone — un numero inventato non può diventare il "prima" di
 * un salto. Quante siano lo dice chi rende la lista (`righeDalServer`),
 * perché non è sempre una pagina da venti: la classifica finale arriva tutta
 * in una volta con `arena:ended` — fino a cento righe, che è il tetto del
 * server — e lì i posti sono veri fino all'ultima riga.
 *
 * Il prezzo è reale, non nullo, e va detto per intero: sotto la soglia si
 * spengono **anche frecce vere**. Da lì in giù, per il client, il sorpasso e
 * l'espulsione sono lo stesso evento — un posto in pagina che si libera — e
 * non sono distinguibili. Si perdono quindi:
 * - chi scivola dalla ventesima alla ventunesima e la sua ▼1: la sua riga è
 *   indistinguibile da quella del fantasma;
 * - chi risale dalla coda dentro la pagina, la prima volta: il suo "prima" è
 *   un numero del client, che a volte è perfino giusto ma non si sa quando;
 * - le frecce della coda quando a liberare il posto è un sorpasso e non un
 *   ritiro. Misurato: arena da 25 con "Mostra altri" premuto e nessuna
 *   espulsione, `u25` vince due partite e sale dal venticinquesimo al
 *   ventesimo posto; prima si accendevano `u25 ▲5` e cinque `▼1` sulle righe
 *   20-24, **tutte e sei vere**. Adesso niente.
 *
 * Nessuna delle tre si recupera finché il server non manda anche i posti
 * della coda.
 *
 * Perché allora una prop e non la costante? Perché la soglia è un fatto di
 * chi rende la lista, non della lista. La pagina dei risultati finali riceve
 * col verdetto una classifica che è intera per costruzione — `arena:ended` ha
 * un tetto di cento righe, e il verdetto si dichiara solo quando ci stanno
 * tutte — e la rende tutta: lì nessun numero è inventato, e fermarsi alla
 * ventesima riga sarebbe una regola presa in prestito da un altro schermo.
 * Oggi non si vede la differenza: quella pagina, quando si aggiorna, lo fa
 * solo accodando la coda di «Mostra altri», che non sposta i posti delle righe
 * già a schermo, e una freccia nasce da un posto che cambia (misurato). Ma è
 * la differenza fra tacere per costruzione e tacere per fortuna, e la prima
 * volta che quella classifica si aggiornasse da sola la fortuna finirebbe.
 */
function useRankChanges(
  standings: ArenaStandingEntry[],
  righeDalServer: number,
): Map<string, number> {
  const prevRanksRef = useRef<Map<string, number>>(new Map());
  const [deltas, setDeltas] = useState<Map<string, number>>(new Map());
  /*
   * Il timer sta in un ref e non nel cleanup dell'effetto. Il cleanup girava a
   * ogni classifica — cioè ogni due secondi — e annullava lo spegnimento prima
   * che scattasse. A schermo la freccia sfumava comunque — l'animazione di
   * comparsa finisce a opacity 0 e ci resta, `forwards` — ma il suo nodo non
   * se ne andava più dal DOM, e un nodo invisibile ha ancora il suo
   * `aria-label`: a chi legge con la voce continuava ad annunciare un salto
   * finito da un pezzo. E siccome non si smontava, la seconda risalita dello
   * stesso passo (▲1 dopo ▲1) riusava quel nodo già sfumato invece di
   * crearne uno nuovo: l'animazione non ripartiva e a schermo non si vedeva
   * niente (A16).
   */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prevRanks = prevRanksRef.current;
    const newDeltas = new Map<string, number>();

    /* Le righe vere: quelle che arrivano dal server con la loro posizione.
       Quelle dopo hanno un numero messo dal client e qui non contano. */
    const dalBroadcast = standings.slice(0, righeDalServer);

    for (const entry of dalBroadcast) {
      const prev = prevRanks.get(entry.userId);
      if (prev !== undefined && prev !== entry.rank) {
        // Positive delta = moved up (rank decreased), negative = moved down
        newDeltas.set(entry.userId, prev - entry.rank);
      }
    }

    /*
     * Le posizioni si salvano sempre, anche quando non è cambiato niente.
     * Prima il ramo con le frecce usciva senza aggiornare `prevRanksRef` in
     * caso di cleanup, e la stessa freccia si ripresentava a ogni giro (B9).
     */
    const nextRanks = new Map<string, number>();
    for (const entry of dalBroadcast) {
      nextRanks.set(entry.userId, entry.rank);
    }
    prevRanksRef.current = nextRanks;

    if (newDeltas.size === 0) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setDeltas(newDeltas);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setDeltas(new Map());
    }, DELTA_DISPLAY_MS);
  }, [standings, righeDalServer]);

  /* L'unico cleanup che ha senso: allo smontaggio, non a ogni classifica. */
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return deltas;
}

/**
 * Il segno «con questo non ti riaccoppio», col tempo che manca.
 *
 * ⚠️ E' un componente a se' e non tre righe dentro `StandingsRow`, e la ragione
 * e' l'unica che conta qui: il conto alla rovescia ticchetta una volta al
 * secondo, e dentro la riga farebbe ridisegnare la riga — cioe' annullerebbe
 * il `memo` proprio sulle righe che qualcuno sta guardando. Isolato cosi', a
 * ridisegnarsi e' solo questo pezzetto, e solo sulle una-o-due righe segnate.
 *
 * E' la stessa scelta gia' fatta a livello di pagina: i countdown dell'arena
 * vivono in `ScheduledCountdown`/`ActiveCountdown` e non in `ArenaDetailPage`,
 * per non far ri-renderizzare la classifica intera al secondo.
 *
 * `useExpiryCountdown` proietta ogni volta da `Date.now()` invece di
 * decrementare, quindi una scheda tornata dal background riprende col numero
 * vero e non con uno che non e' mai esistito.
 *
 * A zero si toglie di mezzo da solo. Il server manda l'elenco aggiornato entro
 * un giro di accoppiamento (2s), ma se quel giro e' fermo — giocatore in pausa,
 * arena finita — senza questa uscita il segno resterebbe acceso per sempre su
 * un divieto gia' scaduto.
 */
const SegnoRivincita = memo(function SegnoRivincita({ fino }: { fino: number }) {
  const { t } = useTranslation();
  const secondi = useExpiryCountdown(fino);
  if (secondi <= 0) return null;

  /* `formatTime` vuole millisecondi e arrotonda per eccesso: i secondi interi
     che arrivano da qui ci passano senza perdere niente. */
  const quanto = formatTime(secondi * 1000);
  /* Il numero e' dentro l'elemento etichettato, quindi l'etichetta se lo porta
     dietro: un lettore di schermo legge una frase sola, non l'icona e poi una
     cifra staccata. */
  const etichetta = t('arena.standings.justPlayedIn', { time: quanto });

  return (
    <span
      className="arena-recent-badge"
      data-testid="arena-standings-excluded"
      role="img"
      aria-label={etichetta}
      title={etichetta}
    >
      <RotateCcw size={13} aria-hidden="true" />
      <span className="arena-recent-time" data-testid="arena-standings-excluded-time">{quanto}</span>
    </span>
  );
});

interface StandingsRowProps {
  player: ArenaStandingEntry;
  isMe: boolean;
  delta: number | undefined;
  isActive: boolean;
  /** Solo per la propria riga: le altre non hanno un distacco da mostrare. */
  distacco?: Distacco;
  /**
   * Quando questo giocatore torna accoppiabile, sull'orologio locale.
   * Assente = non c'e' nessun divieto in corso verso di lui.
   *
   * ⚠️ Un NUMERO e non l'oggetto `{ userId, fino }`, e non e' pigrizia: la riga
   * e' `memo` e confronta le props per riferimento, quindi un oggetto nuovo a
   * ogni classifica la ridisegnerebbe sempre. Un numero si confronta per
   * valore, e finche' la scadenza e' la stessa la riga sta ferma — anche
   * mentre il conto alla rovescia dentro di lei scorre.
   */
  esclusoFino?: number;
  /**
   * Presente solo quando guardare quella partita è concesso: la riga non
   * decide chi possa farlo, lo decide chi rende la lista. Così il memo della
   * riga confronta un riferimento stabile — la stessa callback, oppure niente.
   */
  onSpectate?: (userId: string) => void;
  /**
   * Apre il profilo pubblico di quel giocatore. Come `onSpectate`, la riga non
   * decide chi abbia un profilo da aprire: senza la callback il nome resta
   * testo, e il memo confronta comunque un riferimento stabile.
   */
  onOpenProfile?: (username: string) => void;
  /**
   * Nessuno ha ancora giocato: il posto non si scrive, e al suo posto c'e' un
   * trattino.
   *
   * Prima dell'avvio le posizioni di un'arena escono da un `ROW_NUMBER()`
   * ordinato per `user_id`: uno e' primo e un altro secondo perche' cosi' sono
   * venuti gli UUID, e i primi tre portavano oro, argento e bronzo. Lo decide
   * chi rende la lista e non la riga, perche' e' una domanda su TUTTA la
   * classifica (`nessunoHaGiocato`) — e perche' un BOOLEANO si confronta per
   * valore, che e' la regola di questo `memo` (vedi `esclusoFino`).
   */
  postoMuto: boolean;
  /**
   * Gli spareggi da DIRE su questa riga, e solo quelli che hanno deciso
   * qualcosa.
   *
   * Tre booleani e non un oggetto, per la regola di questo `memo` (vedi
   * `esclusoFino`): un oggetto nuovo a ogni classifica ridisegnerebbe tutte le
   * righe. E li decide chi rende la lista, non la riga, perche' sono domande
   * sul GRUPPO di pari punti — una riga da sola non sa con chi e' in parita'.
   *
   * `mostraSd` e `mostraAvversari` sono alternativi: si mostra il primo
   * criterio che separa il gruppo, non tutti. Se ha deciso lo scontro diretto,
   * la media degli avversari non c'entra niente con l'ordine che si vede, e
   * metterla accanto sarebbe rumore su una riga larga 360px.
   */
  mostraSd: boolean;
  mostraAvversari: boolean;
  /** Nessuno dei cinque criteri ha separato: sono arrivati PARI. */
  pariMerito: boolean;
}

const StandingsRow = memo(forwardRef<HTMLLIElement, StandingsRowProps>(
  function StandingsRow({ player, isMe, delta, isActive, distacco, esclusoFino, onSpectate, onOpenProfile, postoMuto, mostraSd, mostraAvversari, pariMerito }, ref) {
    const { t } = useTranslation();
    const deltaClass = delta
      ? delta > 0
        ? 'rank-up'
        : 'rank-down'
      : '';

    /* Lo stesso ordine di precedenza dei simboli qui sotto, in una parola sola. */
    const statoRiga = player.isWithdrawn
      ? 'withdrawn'
      : player.isPaused
      ? 'paused'
      : player.isPlaying
      /*
       * `isPlaying` prima di `offline`: una partita aperta scrive
       * `is_available = FALSE` apposta, e chi sta giocando non e' scollegato.
       */
      ? 'playing'
      : player.isOnline === false
      /*
       * Non collegato: iscritto, nessuna partita aperta e nessun socket suo.
       * Finiva in `'quiet'`, cioe' in una riga muta identica a quella di chi
       * e' collegato e aspetta un avversario — e chi guardava la classifica
       * non aveva modo di sapere perche' quel nome non giocasse mai. Succede a
       * chi cade a meta' arena e a chi si era prenotato e poi ha spento il
       * telefono.
       *
       * `=== false` e non `!player.isOnline`: `undefined` vuol dire «un
       * payload piu' vecchio del campo, non si sa», e una pastiglia che dice
       * «non in linea» per mancanza di informazioni la direbbe a tutti.
       */
      ? 'offline'
      : 'quiet';

    /*
     * TUTTE le partite, non le ultime otto: la fila ha una riga sua (vedi il
     * secondo piano, più sotto) e il taglio lo decide il foglio, che la
     * larghezza la conosce. Fino al 19 ottobre 2026 c'era qui una
     * `slice(-8)` — un tetto di larghezza deciso dove la larghezza non si sa.
     *
     * Lo storico si mostra solo se è COMPLETO. Il ripiego su V/G non è un caso
     * limite di comodo: i partecipanti nati prima della migration 071 hanno lo
     * storico vuoto o più corto delle partite giocate, e una fila di tre pedine
     * accanto a dieci punti è una bugia — è la promessa scritta nel commento
     * della 071, che il codice fino a oggi manteneva solo a metà (guardava
     * `length > 0`).
     */
    const storico = player.pointsHistory ?? [];
    const conFila = storico.length > 0 && storico.length === player.gamesPlayed;

    /*
     * IL POSTO CHE SI MOSTRA e' `posizione`, non `rank`.
     *
     * `rank` e' l'ordine delle righe — stretto, mai pari — e serve alla
     * paginazione e alle frecce ▲▼. `posizione` e' il merito: pareggia quando
     * due righe sono arrivate pari su tutti e cinque i criteri sportivi, e
     * allora la classifica dice 1, 1, 3. E' questo il numero che la pedina
     * porta dentro, e sono due ori e nessun argento.
     *
     * Il ripiego su `rank` e' la finestra di deploy: un payload piu' vecchio
     * del campo non lo porta (vedi `ArenaStandingEntry.posizione`).
     */
    const postoMostrato = player.posizione ?? player.rank;

    /* Senza un posto non c'e' nemmeno un podio: vedi `postoMuto`. */
    const medaglia = postoMuto ? null : medagliaDelPosto(postoMostrato);

    const puoAprireProfilo = Boolean(onOpenProfile) && !isGuestUsername(player.username);

    /* Il terzo piano si dichiara solo se c'è qualcosa da metterci: una
     * traccia vuota lascerebbe comunque il suo `row-gap` in mezzo alla riga. */
    /*
     * Il distacco (solo sulla propria riga, nessun altro pezzo sa dirlo) e gli
     * spareggi delle righe in PARITA'.
     *
     * Gli spareggi non stanno su tutte le righe, e non e' per risparmiare
     * spazio: su una riga che non ha nessuno a pari punti non spiegano niente,
     * perche' li' l'ordine l'hanno deciso i punti. Compaiono dove c'e'
     * davvero una parita' da sciogliere — ed e' anche l'unico posto dove
     * qualcuno li cerca, cioe' quando vuole sapere perche' e' secondo e non
     * primo. (Qui stava anche la serie, finche' l'arena ne aveva una.)
     */
    const conFatti = (isMe && Boolean(distacco)) || mostraSd || mostraAvversari || pariMerito;

    return (
      /*
       * `data-*` accanto ai testid: lo stato della riga (mia, podio, verso del
       * salto) va letto senza passare dalle classi, che il restyle rinomina.
       */
      <li
        ref={ref}
        className={['arena-row', isMe ? 'my-row' : '', conFatti ? 'has-facts' : '', deltaClass].filter(Boolean).join(' ')}
        data-testid="arena-standings-row"
        /* Il posto di MERITO, non l'ordine delle righe: e' il numero che si
           legge a schermo, e a parita' piena due righe lo condividono. */
        data-rank={postoMostrato}
        data-me={isMe ? 'true' : undefined}
        data-podium={postoMostrato <= 3 ? 'true' : undefined}
        data-state={statoRiga}
      >
        {/*
          * Il segno di posizione scavalca entrambi i piani della riga: è
          * l'unica cosa che identifica il giocatore prima ancora del nome.
          * La pausa non lo sostituisce più — con un secondo piano a
          * disposizione può dirsi a parole senza rubare la posizione.
          */}
        <span className="arena-row-mark">
          {medaglia ? (
            /* La stessa pedina dei tornei e delle classifiche: il perche' della
               forma sta nella testata di PedinaPodio. Il numero dentro e' il
               posto di MERITO, quindi a parita' piena due righe dicono 1 e 1. */
            <PedinaPodio rank={postoMostrato} testId="arena-standings-medal" />
          ) : (
            /* Il trattino non si fa leggere: un «—» ad alta voce e' rumore, e
               la riga continua a dire nome, punti e partite. */
            <span className="arena-rank-number" data-testid="arena-standings-rank" aria-hidden={postoMuto || undefined}>
              {postoMuto ? POSTO_MUTO : postoMostrato}
            </span>
          )}
          {delta !== undefined && delta !== 0 && (
            <span
              data-testid="arena-standings-delta"
              data-direction={delta > 0 ? 'up' : 'down'}
              className={`arena-rank-delta ${delta > 0 ? 'up' : 'down'}`}
              aria-label={
                delta > 0
                  ? t('tournament.standings.columns.rank') + ' +' + delta
                  : t('tournament.standings.columns.rank') + ' ' + delta
              }
            >
              {delta > 0 ? `▲${delta}` : `▼${Math.abs(delta)}`}
            </span>
          )}
        </span>

        <span className="arena-row-who">
          {/*
            * Il nome è una porta: ci si apre il profilo pubblico, come dalla
            * scheda giocatore in partita e dal replay.
            *
            * L'annuncio «la tua posizione» sta FUORI dal bottone, e non più
            * dentro il nome: `aria-label` sostituisce tutto il contenuto
            * dell'elemento, quindi lì dentro chi ascolta lo perderebbe.
            * `.arena-sr-only` è `position: absolute`, quindi non è un flex
            * item di `.arena-row-who` e non porta con sé nessun `gap`.
            *
            * Il filtro sull'ospite è di scorta: in arena non entrano né
            * ospiti né bot (arena.handler.ts), ma il loro profilo è un 404
            * voluto e una riga `Guest_*` di un'arena vecchia non deve
            * offrire una porta che dà sul vuoto.
            */}
          {puoAprireProfilo ? (
            <button
              type="button"
              className="arena-player-name arena-player-name--link"
              data-testid="arena-standings-name"
              onClick={() => { onOpenProfile!(player.username); }}
              aria-label={t('profile.viewProfileOf', { username: player.username })}
            >
              {player.username}
            </button>
          ) : (
            <span className="arena-player-name" data-testid="arena-standings-name">
              {player.username}
            </span>
          )}
          {isMe && <span className="arena-sr-only"> — {t('tournament.lobby.yourPosition')}</span>}
          {/*
            * Con questo non verrai riaccoppiato, e per quanto ancora.
            *
            * Sta accanto al nome, dove stanno serie e boost, e non fra le
            * pastiglie del secondo piano: li' a 360px ci sono gia' lo score
            * sheet a otto pedine e «In gioco», e una parola in piu' strizza la
            * riga. La legenda sotto la lista spiega il simbolo, perche' un
            * simbolo senza spiegazione e' gia' stato provato qui (i due
            * pallini "in coda" e "offline") ed e' stato tolto.
            *
            * ⚠️ Si rende SEMPRE quando c'e' una scadenza: a decidere se
            * mostrarsi e' `SegnoRivincita`, che e' anche l'unica cosa in
            * questa lista a ticchettare. Vedi la sua dichiarazione.
            */}
          {esclusoFino != null && <SegnoRivincita fino={esclusoFino} />}
          {/*
            * Il boost sta accanto al nome, dopo la serie quando c'è: è la
            * riga che si legge davvero, e chi rischia adesso è una notizia,
            * non un dettaglio da secondo piano.
            *
            * Lo tiene a bada `isPlaying`: il boost vive quanto la partita, e
            * chi non ne ha una aperta non può averlo — anche se il dato
            * arrivasse sporco.
            */}
          {player.isPlaying && player.boostActive && (
            <span
              className="arena-boost-badge"
              data-testid="arena-standings-boost"
              role="img"
              aria-label={t('arena.standings.boostOn')}
            >
              <Zap size={14} aria-hidden="true" />
            </span>
          )}
        </span>

        <span className="arena-row-pts" data-testid="arena-standings-points">{player.points}</span>

        <span className="arena-row-meta">
          {/*
            * Senza uno storico completo restano le vinte sulle giocate: vedi
            * `conFila`. È il primo pezzo del piano, dove stava la fila prima
            * che prendesse una riga sua.
            */}
          {!conFila && (
            <span className="arena-score-fallback" data-testid="arena-standings-games">
              {player.gamesWon}/{player.gamesPlayed}
            </span>
          )}

          {/*
            * Restano i quattro stati su cui c'è qualcosa da dire, tutti
            * scritti a parole. "In coda" e "offline" erano due pallini
            * colorati che nessuna legenda spiegava e su cui non si può agire:
            * in arena l'avversario non lo scegli, quindi sapere che è libero
            * non serve.
            *
            * "Non in linea" torna, ma come parola e non come pallino: su
            * questo c'è qualcosa da dire, perché spiega l'unica cosa che la
            * classifica altrimenti non racconta — un nome che non gioca mai.
            * "In coda" resta fuori: è lo stato normale, e dirlo a tutti non
            * distingue nessuno.
            */}
          {isActive && statoRiga !== 'quiet' && (
            <span className="arena-player-state" data-testid="arena-standings-status" data-state={statoRiga}>
              {player.isWithdrawn ? (
                <><LogOut size={13} aria-hidden="true" />{t('tournament.standings.status.left')}</>
              ) : player.isPaused ? (
                <><Pause size={13} aria-hidden="true" />{t('tournament.standings.status.paused')}</>
              ) : statoRiga === 'offline' ? (
                <><WifiOff size={13} aria-hidden="true" />{t('tournament.standings.status.offline')}</>
              ) : (
                /* `tournament.standings.playing` non è mai esistita in nessuna
                   delle sette lingue: la pastiglia mostrava la chiave nuda da
                   560px in su. La forma buona è sotto `status`, come le altre. */
                <><Swords size={13} aria-hidden="true" />{t('tournament.standings.status.playing')}</>
              )}
            </span>
          )}

          {/*
            * La porta per guardare. Sta in fondo al secondo piano perché è
            * l'unica cosa della riga su cui si può agire, e le azioni stanno
            * dove finisce la lettura.
            *
            * `isPlaying` è la condizione vera: senza una partita aperta non
            * c'è niente da guardare, e il server risponderebbe `NOT_PLAYING`
            * (`spectator.handler.ts`). Chi possa vederla lo decide invece
            * `onSpectate` — vedi `occhioConcesso` più sotto.
            *
            * Solo l'icona: la parola accanto ci starebbe a 900px ma non a
            * 360, e questa riga a 360 è già al suo tetto. Il nome della cosa
            * resta nell'`aria-label`, che è dove serve a chi l'icona non la
            * vede.
            */}
          {onSpectate && player.isPlaying && (
            <button
              type="button"
              className="arena-watch-btn"
              data-testid="arena-standings-watch"
              aria-label={t('spectate.watchPlayer', { username: player.username })}
              title={t('spectate.watchPlayer', { username: player.username })}
              onClick={() => onSpectate(player.userId)}
            >
              <Eye size={16} aria-hidden="true" />
            </button>
          )}

          {/*
            * Lo score sheet: una pedina per partita, la più recente a destra,
            * e la somma è il totale della colonna accanto.
            *
            * ULTIMO di questo piano, e larga quanto la riga (`flex-basis: 100%`
            * nel foglio): così si prende una riga sua sotto stato e occhio,
            * dove ci stanno ~16 pedine invece delle ~7 che le restavano quando
            * divideva il piano con la pastiglia. Ultimo nel DOM e non in mezzo:
            * stato e occhio restano sulla riga dove stanno sempre stati.
            */}
          {conFila && (
            <ScoreSheet
              esiti={storico.map(esitoDi)}
              testId="arena-standings-sheet"
              className="arena-score-sheet"
            />
          )}
        </span>

        {/*
          * Il terzo piano esiste solo per la propria riga. Non è un dettaglio
          * che si apre con un gesto: è l'unica riga che si legge davvero, e
          * queste tre cose servono sempre — quindi stanno sempre.
          */}
        {conFatti && (
          <span className="arena-row-facts" data-testid="arena-standings-facts">
            {distacco && (
              <span className="arena-fact" data-testid="arena-standings-fact-gap">
                {distacco.verso === 'salire'
                  ? t('arena.standings.toClimb', { count: distacco.punti })
                  : t('arena.standings.lead', { count: distacco.punti })}
              </span>
            )}
            {/*
              * Lo scontro diretto, col segno: «+1» vuol dire che ne ha battuto
              * uno piu' di quanti lo hanno battuto, fra i suoi pari punti.
              *
              * La sigla «SD» e' la stessa del girone
              * (`tournament.standings.sdSigla`) perche' e' la stessa cosa; il
              * nome per intero sta nel `title` e in un pezzo per i lettori di
              * schermo, perche' qui sopra non c'e' nessuna intestazione di
              * colonna a spiegarla.
              *
              * ⚠️ Non si dice CHI ha battuto: il girone lo fa (`superatiDa`),
              * ma quello nasce dalle partite, e in arena il client non le ha.
              * Un saldo con segno e' quello che si puo' dire con verita'.
              */}
            {mostraSd && (
              <span
                className="arena-fact"
                data-testid="arena-standings-fact-sd"
                title={t('arena.standings.tiebreakH2hTitle')}
              >
                <b aria-hidden="true">{t('tournament.standings.sdSigla')}</b>
                <span className="arena-sr-only">{t('arena.standings.tiebreakH2hTitle')} </span>
                {conSegno(player.h2hSaldo ?? 0)}
              </span>
            )}
            {/* `decimale` e non `toFixed`: la media e' quasi sempre
                frazionaria, e in mezza Europa il separatore e' la virgola. */}
            {mostraAvversari && (
              <span
                className="arena-fact"
                data-testid="arena-standings-fact-opp"
                title={t('arena.standings.tiebreakOppTitle')}
              >
                <b aria-hidden="true">{t('arena.standings.tiebreakOpp')}</b>
                <span className="arena-sr-only">{t('arena.standings.tiebreakOppTitle')} </span>
                {decimale(player.oppPointsAvg ?? 0)}
              </span>
            )}
            {/* Nessuno dei cinque criteri li ha separati: non e' arrivato
                dietro, e' arrivato PARI. La parola e' la stessa dello
                svizzero. */}
            {pariMerito && (
              <span className="arena-fact" data-testid="arena-standings-fact-tied">
                {t('tournament.standings.tied')}
              </span>
            )}
          </span>
        )}
      </li>
    );
  }
));

export default function ArenaStandings({
  standings,
  userId,
  isActive,
  excluded,
  righeDalServer = PAGINA_CLASSIFICA,
  righeInClassifica,
  onLoadMore,
  isLoadingMore,
  onSpectatePlayer,
}: ArenaStandingsProps) {
  const { t } = useTranslation();
  const myRowRef = useRef<HTMLLIElement>(null);
  const rankDeltas = useRankChanges(standings, righeDalServer);
  const [cerca, setCerca] = useState('');

  /*
   * ── Il profilo, sopra l'arena ────────────────────────────────────────
   * Un overlay e non `setScreen('profile')`: il ramo `profile` di App.tsx ha
   * `onBack` cablato sulla home, quindi tornare indietro dal profilo
   * lascerebbe l'arena — quale arena, quale scheda — per strada. Da qui invece
   * la classifica resta viva sotto, e `FullscreenPanel` porta già gesto
   * «indietro», Escape e chiusura sul backdrop.
   *
   * Lo stato sta nel componente e non nei due host (dettaglio e risultati
   * finali): così la porta si apre in tutt'e due senza duplicare niente.
   */
  const [profileFor, setProfileFor] = useState<string | null>(null);
  /* Stabili: il `memo` di `StandingsRow` confronta questi riferimenti, e una
     callback nuova a ogni classifica ridisegnerebbe tutte le righe. */
  const apriProfilo = useCallback((username: string) => { setProfileFor(username); }, []);
  const chiudiProfilo = useCallback(() => { setProfileFor(null); }, []);

  /* Se c'è una pagina dopo, e — solo quando lo si sa — quante righe manchino.
     Vedi `codaDaOffrire` per il ripiego quando il numero non arriva. */
  const coda = codaDaOffrire(righeInClassifica, standings.length, righeDalServer);

  const distacco = useMemo(() => calcolaDistacco(standings, userId), [standings, userId]);

  /*
   * Finche' nessuno ha giocato, le posizioni sono quelle degli UUID: vedi
   * `nessunoHaGiocato` e la prop `postoMuto` della riga.
   */
  const postoMuto = useMemo(() => nessunoHaGiocato(standings), [standings]);

  /*
   * ── Quale spareggio ha deciso, gruppo per gruppo ─────────────────────
   *
   * I criteri 3 e 4 (scontro diretto e qualita' degli avversari) sono numeri
   * che non si leggono da nessun'altra parte della riga: punti e V/G ci sono
   * gia', le partite giocate pure. Quindi vanno DETTI — ma solo dove hanno
   * deciso qualcosa.
   *
   * Il gruppo e' `punti + vittorie`, cioe' il sottoinsieme che i due criteri
   * precedenti non hanno separato: e' lo stesso gruppo su cui il server calcola
   * lo scontro diretto, e fuori da li' il saldo non e' un confronto fra pari.
   *
   * Dentro il gruppo si mostra IL PRIMO criterio che separa, non tutti: se ha
   * deciso lo scontro diretto, la media degli avversari non c'entra con
   * l'ordine che si vede, e affiancarla sarebbe rumore su una riga larga
   * 360px. Se non separa nessuno dei due, il gruppo e' pari merito e lo dice
   * `pariMerito` (che si legge da `posizione`, cioe' dal numero che il server
   * ha gia' calcolato, spareggi compresi).
   *
   * Si guarda `standings` INTERO e non le righe filtrate dalla ricerca: la
   * parita' e' un fatto della classifica, non della vista. Cercare un nome non
   * deve far sparire la spiegazione dalla sua riga.
   */
  const spareggiDaDire = useMemo(() => {
    const gruppi = new Map<string, ArenaStandingEntry[]>();
    for (const s of standings) {
      const chiave = `${s.points}|${s.gamesWon}`;
      const gruppo = gruppi.get(chiave);
      if (gruppo) gruppo.push(s);
      else gruppi.set(chiave, [s]);
    }

    const sd = new Set<string>();
    const avversari = new Set<string>();
    for (const gruppo of gruppi.values()) {
      if (gruppo.length < 2) continue;
      const varia = (leggi: (riga: ArenaStandingEntry) => number) => {
        const primo = leggi(gruppo[0]!);
        return gruppo.some((riga) => leggi(riga) !== primo);
      };
      if (varia((riga) => riga.h2hSaldo ?? 0)) {
        for (const riga of gruppo) sd.add(riga.userId);
      } else if (varia((riga) => riga.oppPointsAvg ?? 0)) {
        for (const riga of gruppo) avversari.add(riga.userId);
      }
    }
    return { sd, avversari };
  }, [standings]);

  /*
   * Chi e' arrivato PARI: `posizione` e' il `RANK()` sui soli criteri sportivi,
   * quindi due righe la condividono solo quando nessuno dei cinque le ha
   * separate.
   *
   * ⚠️ Spento quando nessuno ha ancora giocato: a inizio arena sono tutti a
   * zero su tutto, cioe' tutti primi pari merito, e scriverlo su ogni riga
   * sarebbe rumore — e' la stessa ragione di `postoMuto`.
   *
   * La coda oltre le righe che arrivano dal server non entra: `fondiClassifica`
   * la rinumera per posizione e il suo `posizione` e' vecchio. Meglio tacere
   * che dire un pari merito che non c'e' piu'.
   */
  const pariMerito = useMemo(() => {
    if (postoMuto) return new Set<number>();
    const quanti = new Map<number, number>();
    for (const s of standings.slice(0, righeDalServer)) {
      if (s.posizione == null) continue;
      quanti.set(s.posizione, (quanti.get(s.posizione) ?? 0) + 1);
    }
    return new Set([...quanti.entries()].filter(([, n]) => n > 1).map(([posto]) => posto));
  }, [standings, righeDalServer, postoMuto]);

  /*
   * Da elenco a mappa una volta sola: la riga chiede «fino a quando?», e la
   * risposta e' un numero che il suo `memo` confronta per valore.
   *
   * Le scadenze gia' passate non entrano: l'elenco lo rinfresca il server
   * quando cambia, e fra un giro e l'altro (2s) puo' contenere un divieto
   * appena finito. Il segno sa comunque togliersi da solo — e' la legenda qui
   * sotto, che non ticchetta, ad avere bisogno di questo filtro.
   */
  const esclusi = useMemo(() => {
    const ora = Date.now();
    return new Map(
      (excluded ?? []).filter((e) => e.fino > ora).map((e) => [e.userId, e.fino] as const),
    );
  }, [excluded]);

  /*
   * Pieno, mezzo e vuoto si leggono da soli; da che parte si legge la fila e
   * cosa sia l'oro, no — e l'oro è l'unica cosa della riga senza una spiegazione
   * da nessun'altra parte (il boost almeno ha il suo pannello nei Dettagli).
   *
   * Una riga sola, una volta sopra la lista, e solo se c'è uno score sheet da
   * spiegare: con tutti i partecipanti nati prima della migration 071 la
   * classifica mostra V/G, e la legenda parlerebbe di pedine che non ci sono.
   */
  const conScoreSheet = useMemo(
    /* La stessa guardia della riga (`conFila`): storico COMPLETO, o la legenda
       parlerebbe di pedine che per quella riga non si disegnano. */
    () => standings.some((s) => {
      const n = s.pointsHistory?.length ?? 0;
      return n > 0 && n === s.gamesPlayed;
    }),
    [standings],
  );

  /*
   * «Le più vecchie restano fuori a sinistra», e solo quando può succedere.
   *
   * La sfumatura è per riga e sa regolarsi da sé; questa frase sta sopra la
   * lista, una volta per tutte, quindi va detta solo se almeno una fila supera
   * la capienza misurata a 360px. Sotto quella soglia niente è tagliato a
   * nessuna larghezza utile, e dirlo sarebbe una bugia.
   */
  const conTaglio = useMemo(
    () => standings.some((s) => (s.pointsHistory?.length ?? 0) > CAPIENZA_A_360),
    [standings],
  );

  /*
   * ── L'occhio, a chi ──────────────────────────────────────────────────
   * A chiunque, arena in corso compresa: guardare la partita di un altro fa
   * parte dello stare in un'arena, e chi guarda RESTA in coda.
   *
   * Fino al 28 Ago l'occhio si nascondeva a chi avesse una riga in questa
   * graduatoria — cioe' a quasi tutti quelli che avevano motivo di usarlo.
   * Doveva rendere scomodo lo spionaggio del rivale, ma quella stessa regola
   * dichiarava di non essere una difesa: dalla lista online si guarda comunque,
   * e la porta restava aperta una schermata piu' in la'. Costava una funzione a
   * tutti per un dosso che chiunque poteva aggirare.
   *
   * Chi e' in PARTITA ci passa, e dal 10 Ott: `ClassificaArenaInPartita` monta
   * questa lista dentro la schermata della partita d'arena (il punteggio
   * dell'intestazione la apre). Prima qui c'era scritto il contrario — «non
   * passa di qui, App.tsx tiene su 'arena-game' chi ha matchFound o playing» —
   * e quella era la ragione per cui non serviva una guardia.
   *
   * Adesso la guardia e' la callback stessa: chi monta la lista mentre si
   * gioca NON la passa, e l'occhio non si disegna. Resta la difesa vera, che
   * non dipende da quale schermata si stia disegnando: la guardia `BUSY` di
   * `game/spectator.handler.ts` rifiuta di far guardare chi sta giocando,
   * perche' le due damiere escono dallo stesso `useGameStore`.
   */
  const occhioConcesso = Boolean(onSpectatePlayer);

  const mostrate = useMemo(() => {
    const ago = cerca.trim().toLowerCase();
    if (!ago) return standings;
    return standings.filter((s) => s.username.toLowerCase().includes(ago));
  }, [standings, cerca]);

  /*
   * La ricerca vede solo le righe CARICATE, e per un'arena da sessanta a
   * schermo ce ne sono venti.
   *
   * Cercare il quarantacinquesimo dava «Nessun giocatore con questo nome.» —
   * una frase che sembra una risposta definitiva — e insieme faceva sparire la
   * fascia «+40 altri giocatori · Mostra altri», perche' era appesa a
   * `!cerca`. Cosi' non restava ne' l'informazione che mancava mezza
   * graduatoria, ne' il modo di chiederla: l'unica lettura possibile era che
   * quel giocatore nell'arena non ci fosse.
   *
   * Adesso la fascia resta durante la ricerca — e' proprio il momento in cui
   * serve di piu' — e sopra si dice a parole dove sta guardando la ricerca.
   */
  const ricercaAttiva = cerca.trim().length > 0;

  /* Si guarda fra le righe MOSTRATE, non fra tutti gli esclusi: la legenda
     spiega un simbolo, e se quel simbolo la ricerca l'ha portato via non c'e'
     niente da spiegare. */
  const conEsclusi = useMemo(
    () => mostrate.some((s) => esclusi.has(s.userId)),
    [mostrate, esclusi],
  );

  /* Come sopra, e per la stessa ragione: la frase spiega i numeri che si
     vedono, quindi si dice solo se almeno una riga a schermo ne porta uno —
     un fatto di spareggio, o un pari merito. */
  const conSpareggi = useMemo(
    () => mostrate.some((s) => (
      spareggiDaDire.sd.has(s.userId)
      || spareggiDaDire.avversari.has(s.userId)
      || (s.posizione != null && pariMerito.has(s.posizione))
    )),
    [mostrate, spareggiDaDire, pariMerito],
  );

  // Scroll user's row into view when it changes position
  const scrollToMyRow = useCallback(() => {
    if (myRowRef.current) {
      myRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  /*
   * Una volta sola, all'arrivo: le dipendenze mancanti erano volute, ma
   * `standings` e `userId` servono per sapere *se* c'è una riga da cercare.
   * Il flag impedisce che il salto si ripeta a ogni aggiornamento.
   */
  const giaSaltato = useRef(false);
  useEffect(() => {
    if (giaSaltato.current) return;
    if (!userId || !standings.some((s) => s.userId === userId)) return;
    giaSaltato.current = true;
    const timer = setTimeout(scrollToMyRow, SCROLL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [standings, userId, scrollToMyRow]);

  if (standings.length === 0) {
    return (
      <div className="arena-standings-empty" data-testid="arena-standings-empty">
        {t('tournament.standings.empty')}
      </div>
    );
  }

  return (
    <div className="arena-standings-component" data-testid="arena-standings">
      {standings.length > SOGLIA_RICERCA && (
        <div className="arena-standings-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
            placeholder={t('arena.standings.search')}
            aria-label={t('arena.standings.search')}
            data-testid="arena-standings-search"
            autoComplete="off"
          />
        </div>
      )}

      {/* Solo quando c'e' davvero altro da caricare: a graduatoria intera la
          ricerca guarda tutti, e dirlo sarebbe rumore. */}
      {ricercaAttiva && coda && (coda.mancanti != null || onLoadMore) && (
        <p className="arena-sheet-hint" data-testid="arena-standings-search-scope">
          {t('arena.standings.searchScope')}
        </p>
      )}

      {mostrate.length === 0 ? (
        <p className="arena-standings-nomatch" data-testid="arena-standings-nomatch">
          {t('arena.standings.searchEmpty')}
        </p>
      ) : (
        <>
          {conScoreSheet && (
            <p className="arena-sheet-hint" data-testid="arena-standings-sheet-hint">
              {t('arena.standings.sheetHint')}
              {/* La frase del taglio si aggiunge a quella, non la sostituisce:
                  la prima dice da che parte si legge e cos'è l'oro — vero
                  sempre — la seconda spiega una sfumatura che a volte non c'è
                  nemmeno. */}
              {conTaglio && <>{' \u00b7 '}{t('tournament.standings.scoreSheet.clipped')}</>}
            </p>
          )}
          {/* La legenda del simbolo, e solo quando un simbolo c'e' davvero da
              spiegare fra le righe a schermo: una riga fissa sarebbe rumore
              per le tre arene su quattro in cui nessuno e' escluso. */}
          {conEsclusi && (
            <p className="arena-sheet-hint" data-testid="arena-standings-excluded-hint">
              {t('arena.standings.justPlayedHint')}
            </p>
          )}
          {/* La catena degli spareggi, a parole, e solo se fra le righe a
              schermo c'e' davvero una parita' da sciogliere: una riga fissa
              sarebbe rumore per le tre arene su quattro in cui i punti
              decidono tutto da soli. Stesso principio della legenda del
              simbolo qui sopra. */}
          {conSpareggi && (
            <p className="arena-sheet-hint" data-testid="arena-standings-tiebreak-hint">
              {t('arena.standings.tiebreakHint')}
            </p>
          )}
          <ul className="arena-standings-rows" data-testid="arena-standings-table">
            {mostrate.map((player) => {
              const isMe = player.userId === userId;
              return (
                <StandingsRow
                  key={player.userId}
                  ref={isMe ? myRowRef : undefined}
                  player={player}
                  isMe={isMe}
                  delta={rankDeltas.get(player.userId)}
                  isActive={isActive}
                  distacco={isMe ? distacco : undefined}
                  esclusoFino={esclusi.get(player.userId)}
                  /* La propria partita si apre dalla propria scacchiera, non
                     da spettatore: il server la rifiuta con `SELF`. */
                  onSpectate={occhioConcesso && !isMe ? onSpectatePlayer : undefined}
                  onOpenProfile={apriProfilo}
                  postoMuto={postoMuto}
                  mostraSd={spareggiDaDire.sd.has(player.userId)}
                  mostraAvversari={spareggiDaDire.avversari.has(player.userId)}
                  pariMerito={player.posizione != null && pariMerito.has(player.posizione)}
                />
              );
            })}
          </ul>
        </>
      )}

      {/*
        * "+N altri giocatori" diceva una cosa che non era vera: contava le
        * righe che aveva, non quelle che mancavano (B10). Ora il numero viene
        * dal conteggio del server, e accanto c'è il modo di vederle.
        *
        * E il conteggio è quello della graduatoria, non degli iscritti (B1):
        * sono due insiemi diversi, e sull'insieme sbagliato la fascia
        * spariva proprio nelle arene dove più serviva.
        */}
      {coda && (coda.mancanti != null || onLoadMore) && (
        <div className="arena-more-players" data-testid="arena-standings-more">
          {/* La quantità solo quando la si conosce. Senza, resta il bottone:
              e senza nemmeno quello non c'è niente da dire, quindi la fascia
              non compare affatto (la guardia qui sopra). */}
          {coda.mancanti != null && (
            <span>
              {t('tournament.standings.morePlayers', { count: coda.mancanti })}
            </span>
          )}
          {onLoadMore && (
            <button
              type="button"
              className="arena-more-btn"
              data-testid="arena-standings-more-btn"
              onClick={onLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? t('common.loading') : t('arena.standings.loadMore')}
            </button>
          )}
        </div>
      )}

      {profileFor && (
        <PlayerProfileOverlay username={profileFor} onClose={chiudiProfilo} />
      )}
    </div>
  );
}
